//src/app/features/questionnaires/edit/routes/routesBuilder.ts
import { produce } from 'immer';
import { flattenDeep, get as lodashGet, unset as lodashUnset } from 'lodash';
import { useSelector } from 'react-redux';
import { TQuestionnaireData } from '../../../../services/responseTypes/index';
import {
  Citizenship,
  TaxRequestType,
} from '../../create/PrepareQuestionnaire/slice';
import { incomeRoutes } from './income';
import { nonUsFinancialAccountsRoutes } from './nonUsFinancialAccounts';
import { personalDetailsRoutes } from './personal_details';
import {
  BaseQuestionnaireRoute,
  FlatRoute,
  QuestionnaireRoutes,
} from './slice';
import { spouseIncomeRoutes } from './spouseIncome';
import { taxesDeductionsRoutes } from './taxesDeductions';
import { additionalQuestionsRoutes } from './additionalQuestions';
import { businessInfoRoutes } from './businessInfo';
import { soleProprietorshipRoutes } from './soleProprietorship';
import { selectPrepareQuestionnaire } from '../../create/PrepareQuestionnaire/slice';
import TaxRequestTarget from '../../create/TaxRequestTarget';
// import { TaxRequestTargetState } from '../../create/PrepareQuestionnaire/slice';

export const initialState: QuestionnaireRoutes = {
  routes: {
    ...personalDetailsRoutes,
    ...incomeRoutes,
    ...spouseIncomeRoutes,
    ...taxesDeductionsRoutes,
    ...nonUsFinancialAccountsRoutes,
    ...businessInfoRoutes,
    ...soleProprietorshipRoutes,
    ...additionalQuestionsRoutes,

    notes: {
      title: 'Notes',
      path: 'notes',
      isValid: undefined,
      notes: {
        title: 'Notes',
        path: 'notes',
        isValid: undefined,
      },
    },
    summary: {
      title: 'Summary',
      path: 'summary',
      isValid: undefined,
      summary: {
        title: 'Summary',
        path: 'summary',
        isValid: undefined,
      },
      documentsChecklist: {
        title: 'Documents Checklist',
        path: 'documentsChecklist',
        isValid: undefined,
      },
    },
  },
  flatRoutes: [],
  flatMap: {},
};
/**
 * Evaluate the routes object
 *
 * @param data
 * @returns routes
 */
export function buildRoutes(
  data: TQuestionnaireData,
): QuestionnaireRoutes['routes'] {
  const prepareQuestionnaire = data.prepareQuestionnaire;
  const family =
    data.taxQuestionnaire?.personal_details.the_basics.family.values;
  const citizenship = data.prepareQuestionnaire?.citizenship;
  const spendtDaysOutsideUs = data.prepareQuestionnaire?.daysOutsideUs;
  let keysToDelete: string[] = [];

  if (prepareQuestionnaire.taxRequestType === TaxRequestType.Business) {
    keysToDelete.push(
      ...[
        'personal_details',
        'income',
        'taxesDeductions',
        'nonUsFinancialAccounts',
        'spouseIncome',
        'notes',
      ],
    );
  } else {
    keysToDelete.push(
      ...['businessInfo', 'soleProprietorship', 'additionalQuestions'],
    );
  }
  if (citizenship === Citizenship.Not_US_Citizen_Not_GC_Holder) {
    keysToDelete.push(
      ...[
        'spouseIncome',
        'taxesDeductions',
        'personal_details.the_basics.citizenship',
        'personal_details.the_basics.covid_relief',
        'personal_details.the_basics.dependents',
        'personal_details.the_basics.family',
        'personal_details.where_i_live.history',
        'income.homeSale',
        'income.investments',
        'income.selfEmployment',
      ],
    );
  }
  if (spendtDaysOutsideUs === 'no' || spendtDaysOutsideUs === null) {
    keysToDelete.push(
      ...['personal_details.where_i_live.housing_arrangements'],
    );
  }

  if (prepareQuestionnaire) {
    // Personal Details Configuration
    if (
      prepareQuestionnaire.married === 'no' ||
      prepareQuestionnaire.married === null
    ) {
      keysToDelete.push('personal_details.the_basics.family');
    }
    if (
      prepareQuestionnaire.haveChildren === 'no' ||
      prepareQuestionnaire.haveChildren === null
    ) {
      keysToDelete.push('personal_details.the_basics.dependents');
    }

    // Income Configuration
    if (
      prepareQuestionnaire.paidByEmployer === 'no' ||
      prepareQuestionnaire.paidByEmployer === null
    ) {
      keysToDelete.push('income.wages');
    }
    if (prepareQuestionnaire.paidByEmployer === 'yes') {
      if (
        prepareQuestionnaire.incomeOutsideUs === 'no' ||
        prepareQuestionnaire.incomeOutsideUs === null
      ) {
        keysToDelete.push('income.incomeOutsideOfTheUs');
      }
      if (
        prepareQuestionnaire.incomeFromUs === 'no' ||
        prepareQuestionnaire.incomeFromUs === null
      ) {
        keysToDelete.push('income.incomeFromTheUs');
      }
    }
    //passiveIncome
    // if passive income counter 4 hide passive income
    let passiveIncomeCounter = 0;
    if (
      prepareQuestionnaire.reitermentAccount === 'no' ||
      prepareQuestionnaire.reitermentAccount === null
    ) {
      keysToDelete.push('income.passiveIncome.pension');

      passiveIncomeCounter++;
    }
    if (
      prepareQuestionnaire.recieveDividends === 'no' ||
      prepareQuestionnaire.recieveDividends === null
    ) {
      keysToDelete.push('income.passiveIncome.dividends');

      passiveIncomeCounter++;
    }
    if (
      prepareQuestionnaire.earnInterest === 'no' ||
      prepareQuestionnaire.earnInterest === null
    ) {
      keysToDelete.push('income.passiveIncome.interest');

      passiveIncomeCounter++;
    }
    if (
      prepareQuestionnaire.rentProperty === 'no' ||
      prepareQuestionnaire.rentProperty === null
    ) {
      keysToDelete.push('income.passiveIncome.rentalIncome');

      passiveIncomeCounter++;
    }

    if (passiveIncomeCounter === 4) {
      keysToDelete.push('income.passiveIncome');
    }

    //soldProperty
    if (
      prepareQuestionnaire.soldProperty === 'no' ||
      prepareQuestionnaire.soldProperty === null
    ) {
      keysToDelete.push('income.homeSale');
    }

    //selfEmployment
    if (
      prepareQuestionnaire.selfEmployed === 'no' ||
      prepareQuestionnaire.selfEmployed === null
    ) {
      keysToDelete.push('income.selfEmployment');
    }

    // corporationTrust
    if (
      prepareQuestionnaire.corpOwnership === 'no' ||
      prepareQuestionnaire.corpOwnership === null
    ) {
      keysToDelete.push('income.corporationTrust');
    }
    if (prepareQuestionnaire.corpOwnership === 'yes') {
      if (
        prepareQuestionnaire.ownershipNonUsCorp === 'no' ||
        prepareQuestionnaire.ownershipNonUsCorp === null
      ) {
        keysToDelete.push('income.corporationTrust.nonUsCorpOfPartnership');
      }
      if (
        prepareQuestionnaire.ownershipUsCorp === 'no' ||
        prepareQuestionnaire.ownershipUsCorp === null
      ) {
        keysToDelete.push('income.corporationTrust.usCorpOrPartnership');
      }
      if (
        prepareQuestionnaire.nonUsTrust === 'no' ||
        prepareQuestionnaire.nonUsTrust === null
      ) {
        keysToDelete.push('income.corporationTrust.nonUsTrust');
      }
      if (
        prepareQuestionnaire.usTrus === 'no' ||
        prepareQuestionnaire.usTrus === null
      ) {
        keysToDelete.push('income.corporationTrust.usTrust');
      }
    }
    //investments
    if (
      prepareQuestionnaire.investments === 'no' ||
      prepareQuestionnaire.investments === null
    ) {
      keysToDelete.push('income.investments');
    }

    // Taxes Deductions
    // if (
    //   prepareQuestionnaire.maxmizeDeductions === 'no' ||
    //   prepareQuestionnaire.maxmizeDeductions === null
    // ) {
    //   keysToDelete.push('taxesDeductions.deductions');
    // }
    if (
      prepareQuestionnaire.spouse === 'no' ||
      prepareQuestionnaire.spouse === null ||
      family?.fillingWithSpouse === 'no' ||
      family?.fillingWithSpouse === null
    ) {
      keysToDelete.push('taxesDeductions.taxesPaidSpouse');
    }

    if (
      prepareQuestionnaire.spouse === 'no' ||
      prepareQuestionnaire.spouse === null ||
      (prepareQuestionnaire.spousewageIncome === 'no' &&
        prepareQuestionnaire.spousePension === 'no' &&
        prepareQuestionnaire.spouseDivedends === 'no' &&
        prepareQuestionnaire.spouseIncomeInterest === 'no') ||
      family?.fillingWithSpouse === 'no' ||
      family?.fillingWithSpouse === null
    ) {
      keysToDelete.push('spouseIncome');
    }

    if (prepareQuestionnaire.spouse === 'yes') {
      if (
        prepareQuestionnaire.spousewageIncome === 'no' ||
        prepareQuestionnaire.spousewageIncome === null
      ) {
        keysToDelete.push('spouseIncome.wages');
      }
      if (
        prepareQuestionnaire.spousePension === 'no' ||
        prepareQuestionnaire.spousePension === null
      ) {
        keysToDelete.push('spouseIncome.pension');
      }
      if (
        prepareQuestionnaire.spouseDivedends === 'no' ||
        prepareQuestionnaire.spouseDivedends === null
      ) {
        keysToDelete.push('spouseIncome.dividends');
      }
      if (
        prepareQuestionnaire.spouseIncomeInterest === 'no' ||
        prepareQuestionnaire.spouseIncomeInterest === null
      ) {
        keysToDelete.push('spouseIncome.interest');
      }
    }

    // Financial Accounts Outside US Configuration
    if (
      prepareQuestionnaire.financialAccountsOutsideUs === 'no' ||
      prepareQuestionnaire.financialAccountsOutsideUs === null
    ) {
      keysToDelete.push('nonUsFinancialAccounts');
    }
  }
  const newQuestionnaireRoutes = produce(initialState.routes, (draft) => {
    for (const key of keysToDelete) {
      lodashUnset(draft, key);
    }
  });

  return newQuestionnaireRoutes;
}

/**
 *
 * @param routes
 * @param path a dot path to the property inside the routes object
 * @returns [mainTab, subTab, leaf]
 */
export function unpack(
  routes: QuestionnaireRoutes['routes'],
  dotPath: string,
): [
  BaseQuestionnaireRoute,
  BaseQuestionnaireRoute | undefined,
  BaseQuestionnaireRoute | undefined,
] {
  const [mainTab, subTab, leaf] = dotPath.split('.');
  return [
    lodashGet(routes, mainTab),
    lodashGet(routes, `${mainTab}.${subTab}`),
    lodashGet(routes, `${mainTab}.${subTab}.${leaf}`),
  ];
}
/**
 *
 * @param routes
 * @returns array of flat routes in property order inside the routes object
 */
export function flattenRoutes(
  routes: QuestionnaireRoutes['routes'],
): [FlatRoute[], { [slashPath: string]: number }] {
  let index = 0;
  let routeMap: { [slashPath: string]: number } = {};
  const flat = flattenDeep<FlatRoute>(
    Object.keys(routes).map((mainTab) => {
      const [route, subTabs] = makeFlatRoute(routes, mainTab);
      if (subTabs.length === 0) {
        routeMap[route.slashPath] = index++;
        return route;
      }

      return subTabs.map((subTab) => {
        const [route, leaves] = makeFlatRoute(routes, `${mainTab}.${subTab}`);

        if (leaves.length === 0) {
          routeMap[route.slashPath] = index++;
          return route;
        }

        return leaves.map((leaf) => {
          const [route] = makeFlatRoute(routes, `${mainTab}.${subTab}.${leaf}`);
          routeMap[route.slashPath] = index++;
          return route;
        });
      });
    }),
  );
  return [flat, routeMap];
}

/**
 *
 * @param routes QuestionnaireRoutes['routes']
 * @param path
 * @returns [route, nestedTabs]
 */
function makeFlatRoute(
  routes: QuestionnaireRoutes['routes'],
  dotPath: string,
): [FlatRoute, string[]] {
  const {
    path: x,
    title,
    isValid,
    toggle,
    ...tabs
  } = lodashGet(routes, dotPath) as BaseQuestionnaireRoute;

  const [mainTab, subTab, leaf] = unpack(routes, dotPath);
  const route = {
    isValid,
    mainTab: mainTab.path,
    subTab: subTab?.path,
    leaf: leaf?.path,
    mainTabTitle: mainTab?.title,
    subTabTitle: subTab?.title,
    leafTitle: leaf?.title,
    dotPath: [mainTab.path, subTab?.path]
      .concat(leaf?.path ? leaf.path : [])
      .join('.'),
    slashPath: [mainTab.path, subTab?.path]
      .concat(leaf?.path ? leaf.path : [])
      .join('/'),
  };
  return [route, Object.keys(tabs)];
}
